import { index, modelOptions, mongoose, post, prop } from "@typegoose/typegoose";
import { addSchemaKeyHook, SchemaBase, getDefaultSchemaOptions, SchemaConstructorArgs } from "../schemaBase.js";
import { VersionEventActor } from "./subSchemas/versionEventActor.js";

export {
    VersionEventActor,
    VERSION_EVENT_ACTOR_TYPES,
    type VersionEventActorType,
} from "./subSchemas/versionEventActor.js";

const SCHEMA_KEY = "versionEvents" as const;

/**
 * The type of change that produced this version transition.
 */
export type VersionEventType = "create" | "update" | "delete" | "undelete";

/**
 * Audit sidecar for SCD Type 2 version transitions.
 *
 * One VersionEvent is inserted atomically alongside each new versioned document.
 * It carries the who/what/when metadata for the transition without polluting the
 * versioned document itself.
 */
@index({ recordId: 1 })
@index({ versionId: 1 })
@index({ recordId: 1, versionId: 1 })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "versionEvents" as const satisfies typeof SCHEMA_KEY,
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<VersionEvent>>(...addSchemaKeyHook(SCHEMA_KEY))
export class VersionEvent extends SchemaBase<VersionEvent> {
    static readonly schemaKey = SCHEMA_KEY;
    readonly schemaKey = SCHEMA_KEY;

    public _id!: mongoose.Types.ObjectId;

    /**
     * The shared record ID across all versions of the logical entity.
     * Matches `recordId` on the corresponding versioned document.
     */
    @prop({ type: () => mongoose.Types.ObjectId, required: true })
    public recordId!: mongoose.Types.ObjectId;

    /**
     * The version number of the new document version this event corresponds to.
     */
    @prop({ type: () => Number, required: true })
    public versionId!: number;

    /**
     * The collection that the versioned source document lives in (e.g. "warnings").
     * Named `sourceCollection` to distinguish it from the `versionEvents` collection
     * this document itself is stored in.
     */
    @prop({ type: () => String, required: true })
    public sourceCollection!: string;

    /**
     * The type of change that produced this version.
     */
    @prop({ type: () => String, required: true })
    public eventType!: VersionEventType;

    /**
     * The shared transaction timestamp. Equal to `versionValidFrom` on the new
     * version and `versionValidTo` on the superseded version.
     */
    @prop({ type: () => Date, required: true })
    public timestamp!: Date;

    /**
     * Optional human-readable summary of what changed.
     *
     * TODO: This should become a structured translation key + parameters object
     * rather than a raw string so that audit messages can be rendered in any
     * locale. Introduce a generic `{ key: string; params?: Record<string, unknown> }`
     * type when i18n support is added.
     */
    @prop({ type: () => String, required: false })
    public message?: string;

    /**
     * The actor who triggered the change. Absent for writes with no recorded actor —
     * M2M/access-key sessions, periodic jobs and other internal/system writes.
     */
    @prop({ type: () => VersionEventActor, required: false, _id: false })
    public changedBy?: VersionEventActor;

    constructor(properties?: SchemaConstructorArgs<VersionEvent>) {
        super(properties);
        Object.assign(this, properties);
    }
}
